/**
 * Shim for `react-native-modal`. Renders a full-viewport backdrop (into a
 * portal) with the modal content. Backdrop click, Escape key and swipe-down all
 * map to their RN callbacks. The `className`/`style` from styled(Modal) are
 * applied to the backdrop container so layout props (e.g. justify-content) work.
 */
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { toCss } from '../web/styleUtils';

export interface ModalProps {
  isVisible?: boolean;
  children?: React.ReactNode;
  onBackdropPress?: () => void;
  onBackButtonPress?: () => void;
  onSwipeComplete?: () => void;
  swipeDirection?: string | string[];
  propagateSwipe?: boolean;
  animationIn?: string;
  animationOut?: string;
  backdropOpacity?: number;
  style?: any;
  className?: string;
  testID?: string;
  onModalHide?: () => void;
  avoidKeyboard?: boolean;
  [key: string]: any;
}

const Modal = function Modal({
  isVisible,
  children,
  onBackdropPress,
  onBackButtonPress,
  onSwipeComplete,
  swipeDirection,
  backdropOpacity = 0.5,
  style,
  className,
  testID,
}: ModalProps) {
  const startY = useRef<number | null>(null);

  useEffect(() => {
    if (!isVisible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') (onBackButtonPress ?? onBackdropPress)?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isVisible, onBackButtonPress, onBackdropPress]);

  if (!isVisible) return null;

  const dirs = Array.isArray(swipeDirection) ? swipeDirection : swipeDirection ? [swipeDirection] : [];
  const allowSwipeDown = dirs.includes('down');

  return createPortal(
    <div
      data-testid={testID}
      onClick={(e) => {
        if (e.target === e.currentTarget) onBackdropPress?.();
      }}
      onTouchStart={(e) => (startY.current = e.touches[0]?.clientY ?? null)}
      onTouchEnd={(e) => {
        if (!allowSwipeDown || startY.current == null) return;
        const dy = (e.changedTouches[0]?.clientY ?? 0) - startY.current;
        if (dy > 80) onSwipeComplete?.();
        startY.current = null;
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: `rgba(0,0,0,${backdropOpacity})`,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        animation: 'rn-modal-fade 0.2s ease',
      }}
    >
      
      <div className={className} style={{ display: 'flex', flexDirection: 'column', flex: 1, ...(toCss(style) as any) }}>
        {children}
      </div>
      <style>{'@keyframes rn-modal-fade{from{opacity:0}to{opacity:1}}'}</style>
    </div>,
    document.body,
  );
};

export default Modal;
